import { InputPrice } from '@/components/element/Form/InputPrice';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { useSalesOrderInvalidate } from '@/hooks/api/1260_sales_order';
import { useBillingSchedules, useBillingSchedulesInvalidate } from '@/hooks/api/1350_billing_schedules';
import { useUpdateBillingSchedule } from '@/hooks/api/1370_update_billing_schedule';
import { useDestroyBillingSchedule } from '@/hooks/api/1380_destroy_billing_schedule';
import { BillingSchedule } from '@/hooks/api/models';
import { BillingScheduleLogic, TeamMemberLogic } from '@/hooks/api/models/logics';
import { toastApiError, toastError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentSalesOrder } from '@/providers/SalesOrderProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

export const BillingSchedulesElement = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, meTeamMember } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const { data: billingSchedules } = useBillingSchedules({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const [delTarget, setDelTarget] = useState<BillingSchedule>();
  const {
    mutate: destroyMutate,
    isSuccess: destroyIsSuccess,
    isPending: destroyIsPending,
    error,
  } = useDestroyBillingSchedule();
  const { invalidate } = useBillingSchedulesInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { invalidate: invalidateSalesOrder } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });

  useEffect(() => {
    if (destroyIsSuccess) {
      invalidate();
      invalidateSalesOrder();
    }
  }, [destroyIsSuccess]);

  useEffect(() => setShowLoading(destroyIsPending), [destroyIsPending]);
  useEffect(() => toastApiError(error), [error]);

  const onDeleteClick = () => {
    setShowLoading(true);
    destroyMutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: _so_!.id,
      billing_schedule_id: delTarget!.id,
    });
  };

  return (
    <div className="table-responsive">
      <ModalConfirm
        modalId={`delConfirm`}
        okClicked={() => onDeleteClick()}
        message={
          '削除されたデータは元に戻せません。\n紐づくタスクが発行されている場合は削除ができないので、\n該当タスクを先に削除してください。'
        }
      />

      <table className="table align-items-center">
        <thead>
          <tr>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-10 text-end align-bottom border-end">
              請求月
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-15 text-end align-bottom">
              予定(税抜)
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-15 text-end align-bottom border-end">
              <span className="text-dark">一括請求</span>
              <br />
              実績(税抜)
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-15 text-end align-bottom">
              予定(税抜)
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-15 text-end align-bottom border-end">
              <span className="text-dark">月額請求</span>
              <br />
              実績(税抜)
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-15 text-end align-bottom">
              <span className="text-dark">合計</span>
              <br />
              予定・実績(税抜)
            </th>
            <th className="text-secondary opacity-7 w-5 text-end" style={{ width: 110 }}></th>
          </tr>
        </thead>
        <tbody>
          {billingSchedules?.map((d) => (
            <BillingScheduleRow key={`bs-${d.id}`} d={d} setDelTarget={setDelTarget} />
          ))}

          <tr>
            <td className="text-xs text-dark font-weight-bold text-end border-end">合計金額</td>
            <td className="text-xs text-dark font-weight-bold text-end">
              ¥{BillingScheduleLogic.calcTotalSpotAmount(billingSchedules || []).toLocaleString()}
            </td>
            <td className="text-xs text-dark font-weight-bold text-end border-end">
              ¥{BillingScheduleLogic.calcTotalSpotInvoiceTaskBillingAmount(billingSchedules || []).toLocaleString()}
            </td>
            <td className="text-xs text-dark font-weight-bold text-end">
              ¥{BillingScheduleLogic.calcTotalMonthlyAmount(billingSchedules || []).toLocaleString()}
            </td>
            <td className="text-xs text-dark font-weight-bold text-end border-end">
              ¥{BillingScheduleLogic.calcTotalMonthlyInvoiceTaskBillingAmount(billingSchedules || []).toLocaleString()}
            </td>
            {/*<td className="text-xs text-dark font-weight-bold text-end border-end">*/}
            {/*  ¥{BillingScheduleLogic.calcTotalAmount(billingSchedules || []).toLocaleString()}*/}
            {/*</td>*/}
            {/*<td className="text-xs text-dark font-weight-bold text-end border-end">*/}
            {/*  ¥{BillingScheduleLogic.calcTotalInvoiceTaskBillingAmount(billingSchedules || []).toLocaleString()}*/}
            {/*</td>*/}
            <td className="text-xs text-dark font-weight-bold text-end">
              ¥{BillingScheduleLogic.calcTotalSalesAmount(billingSchedules || []).toLocaleString()}
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

type FormInputs = {
  identifier: string;
  team_id: number;
  sales_order_id: number;
  billing_schedule_id: number;
  year_month: string;
  monthly_amount?: number;
  spot_amount?: number;
};

const BillingScheduleRow = ({
  d,
  setDelTarget
}: {
  d: BillingSchedule;
  setDelTarget: Dispatch<SetStateAction<BillingSchedule | undefined>>;
}) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const [editing, setEditing] = useState<boolean>(false);
  const { mutate, data, error, isPending } = useUpdateBillingSchedule();
  const { invalidate } = useBillingSchedulesInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { invalidate: invalidateSalesOrder } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: _so_!.id,
      billing_schedule_id: d.id,
      year_month: d.year_month,
      spot_amount: d.spot_detail?.amount,
      monthly_amount: d.monthly_detail?.amount,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);

  const onSubmit: SubmitHandler<FormInputs> = (inputData) => {
    setShowLoading(true);
    mutate(inputData);
  };

  useEffect(() => {
    if (data) {
      invalidate();
      invalidateSalesOrder();
      setEditing(false);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      const msgs: string[] = (error as any).response.data.errors || [];
      if (msgs.length > 0) toastError(msgs[0]);
    }
  }, [error]);

  if (!editing) {
    return (
      <tr>
        <td className="text-xs text-dark text-end border-end">{d.year_month}</td>
        {d.spot_detail == null ? (
          <>
            <td className="text-xs text-dark text-end">-</td>
            <td className="text-xs text-dark text-end border-end">-</td>
          </>
        ) : (
          <>
            <td className="text-xs text-dark text-end">¥{BillingScheduleLogic.spotAmount(d).toLocaleString()}</td>
            <td className="text-xs text-dark text-end border-end">
              {d.spot_detail.invoice_task_billing_amount != null ? (
                <>¥{BillingScheduleLogic.spotInvoiceTaskBillingAmount(d).toLocaleString()}</>
              ) : (
                <>-</>
              )}
            </td>
          </>
        )}
        {d.monthly_detail == null ? (
          <>
            <td className="text-xs text-dark text-end">-</td>
            <td className="text-xs text-dark text-end border-end">-</td>
          </>
        ) : (
          <>
            <td className="text-xs text-dark text-end">¥{BillingScheduleLogic.monthlyAmount(d).toLocaleString()}</td>
            <td className="text-xs text-dark text-end border-end">
              {d.monthly_detail.invoice_task_billing_amount != null ? (
                <>¥{BillingScheduleLogic.monthlyInvoiceTaskBillingAmount(d).toLocaleString()}</>
              ) : (
                <>-</>
              )}
            </td>
          </>
        )}
        {/*<td className="text-xs text-dark font-weight-bold text-end border-end">*/}
        {/*  ¥{BillingScheduleLogic.totalAmount(d).toLocaleString()}*/}
        {/*</td>*/}
        {/*<td className="text-xs text-dark font-weight-bold text-end border-end">*/}
        {/*  ¥{BillingScheduleLogic.totalInvoiceTaskBillingAmount(d).toLocaleString()}*/}
        {/*</td>*/}
        <td className="text-xs text-dark font-weight-bold text-end">
          ¥{BillingScheduleLogic.totalSalesAmount(d).toLocaleString()}
        </td>
        <td className="text-end">
          <button
            onClick={() => setEditing((e) => !e)}
            className="btn btn-icon-only bg-gradient-success me-1"
          >
            <i className="fas fa-edit"></i>
          </button>
          <button
            className="btn btn-icon-only bg-gradient-danger"
            data-bs-toggle="modal"
            data-bs-target={`#delConfirm`}
            onClick={() => setDelTarget((_) => d)}
          >
            <i className="fas fa-trash-alt"></i>
          </button>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td className="text-xs text-dark text-end border-end">{d.year_month}</td>
      <td className="text-xs text-wrap pe-0">
        <div className="input-group">
          <Controller
            render={({ field: { onChange, value, ...field } }) => <InputPrice onChange={onChange} value={value} />}
            control={control}
            name="spot_amount"
          />
        </div>
      </td>
      <td className="text-xs text-dark text-end border-end">
        {d.spot_detail && d.spot_detail.invoice_task_billing_amount != null ? (
          <>¥{BillingScheduleLogic.spotInvoiceTaskBillingAmount(d).toLocaleString()}</>
        ) : (
          <>-</>
        )}
      </td>
      <td className="text-xs text-wrap pe-0">
        <div className="input-group">
          <Controller
            render={({ field: { onChange, value, ...field } }) => <InputPrice onChange={onChange} value={value} />}
            control={control}
            name="monthly_amount"
          />
        </div>
      </td>
      <td className="text-xs text-dark text-end border-end">
        {d.monthly_detail && d.monthly_detail.invoice_task_billing_amount != null ? (
          <>¥{BillingScheduleLogic.monthlyInvoiceTaskBillingAmount(d).toLocaleString()}</>
        ) : (
          <>-</>
        )}
      </td>
      {/*<td className="text-xs text-dark font-weight-bold text-end border-end">*/}
      {/*  ¥{BillingScheduleLogic.totalAmount(d).toLocaleString()}*/}
      {/*</td>*/}
      {/*<td className="text-xs text-dark font-weight-bold text-end border-end">*/}
      {/*  ¥{BillingScheduleLogic.totalInvoiceTaskBillingAmount(d).toLocaleString()}*/}
      {/*</td>*/}
      <td className="text-xs text-dark font-weight-bold text-end">
        ¥{BillingScheduleLogic.totalSalesAmount(d).toLocaleString()}
      </td>
      <td className="text-end">
        <button onClick={() => setEditing((e) => !e)} className="btn btn-icon-only btn-outline-secondary me-1">
          <i className="fas fa-undo"></i>
        </button>
        <button onClick={() => handleSubmit(onSubmit)()} className="btn btn-icon-only bg-gradient-dark">
          <i className="fas fa-check-circle"></i>
        </button>
      </td>
    </tr>
  );
};
